import React, { useState } from 'react';
import { client } from 'shared';
import VerifyEligibilityModal, { VerificationForm } from './VerifyEligibilityModal';

export default function useClearinghouseVerification({
    workerId,
    workerName,
    businessLocationId,
    onProceed,
    onError,
    submitLabel,
}: {
    workerId?: number;
    workerName?: string;
    businessLocationId?: number;
    onProceed: () => void;
    onError: (message: string) => void;
    submitLabel?: string;
}) {
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [checking, setChecking] = useState(false);

    function submitWithVerification() {
        if (!workerId || !businessLocationId) {
            onProceed();
            return;
        }
        setChecking(true);
        client(
            `compliance/business-location-clearinghouse-record/needs-verification/?user=${workerId}&business_location=${businessLocationId}`,
        )
            .then((response) => {
                if (response.needs_verification) {
                    setVerifyOpen(true);
                } else {
                    onProceed();
                }
            })
            .catch(() => onError('We could not confirm whether this worker needs to be verified. Please try again.'))
            .finally(() => setChecking(false));
    }

    function submitVerification(form: VerificationForm) {
        if (!workerId || !businessLocationId) return Promise.resolve(null);
        const reportedEligible = form.clearinghouse_status === 'Yes' && form.eligibility_status === 'eligible';
        const formData = new FormData();
        formData.append('user', workerId.toString());
        formData.append('business_location', businessLocationId.toString());
        formData.append('eligibility_date', form.eligibility_date);
        formData.append('expiration_date', form.expiration_date);
        formData.append('clearinghouse_status', form.clearinghouse_status);
        if (form.eligibility_status) {
            formData.append('eligibility_status', form.eligibility_status);
        }
        if (form.screening_document) {
            formData.append('screening_document', form.screening_document, form.screening_document.name);
        }
        return client(
            'compliance/business-location-clearinghouse-record/verify/',
            { method: 'POST', body: formData },
            false,
        )
            .then(() => {
                if (reportedEligible) {
                    onProceed();
                }
                return null;
            })
            .catch(() => {
                onError('There was an error saving the verification. Please try again.');
                return null;
            });
    }

    const verificationModal =
        verifyOpen && workerId && workerName ? (
            <VerifyEligibilityModal
                workerId={workerId}
                workerName={workerName}
                submitVerification={submitVerification}
                onClose={() => setVerifyOpen(false)}
                submitLabel={submitLabel}
            />
        ) : null;

    return { submitWithVerification, checking, verificationModal };
}
